import React, { useRef, useState, useEffect } from "react";
import {
  makeStyles,
  Box,
  Typography,
  IconButton,
  TextField
} from "@material-ui/core";
import BackIcon from "@material-ui/icons/ArrowBack";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import locale from "date-fns/locale/en-US";
import StyledDrawer from "../StyledDrawer";
import OptimizedTooltip from "../../common/OptimizedTooltip";
import CustomDatePicker from "./CustomDatePicker";
import EditDialogActions from "../../common/EditDialogActions";

import { getDateValue, remCalc } from "../../../utils/helpers";
import API from "../../../utils/axios";
import {
  setProjects,
  setProjectsQuantity
} from "../../../store/actions/account";
import { connect } from "react-redux";

import { useSnackbar } from "notistack";
import { setProjectDetails } from "../../../store/actions/project";
import { closeSnackbar } from "../../../store/actions/snackbars";
import UnsavedChangesDialog from "../../dialogs/UnsavedChangesDialog";
import { checkIsURL } from "../../../utils/helpers";
const useStyles = makeStyles(theme => ({
  header: {
    height: 80,
    borderBottom: "1px solid #eee",
    boxSizing: "border-box"
  },
  title: {
    fontSize: "1.25rem",
    lineHeight: "1.4375rem",
    fontWeight: 500
  },
  inputField: {
    width: "100%",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    height: theme.spacing(7.75),
    fontSize: "0.9375rem",
    "& svg:hover": {
      cursor: "pointer"
    }
  },
  picker: {
    zIndex: "100000 !important"
  },
  pickerWrapper: {
    alignItems: "center",
    alignContent: "center",
    justifyContent: "center"
  },
  btnSave: {
    textTransform: "none",
    boxShadow: "none !important",
    minWidth: remCalc(51),
    padding: theme.spacing(1.25, 2),
    lineHeight: "1rem",

    "&.Mui-disabled, &.Mui-disabled:hover": {
      backgroundColor: "#1329FE",
      color: "#fff",
      opacity: "38%"
    }
  },
  btnCancel: {
    color: "#808080",
    minWidth: remCalc(63),
    marginRight: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    lineHeight: "1rem"
  },
  btnGroup: {
    padding: theme.spacing(1.25, 5, 1.25, 0),
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(5)
  },
  weekend: {
    "& button": {
      color: "#B6B6B6"
    }
  }
}));

const dateFns = new DateFnsUtils();

function ProjectsEditDialog({
  open,
  isApp,
  isEditingDuplicate,
  currentProject,
  projectsRef,
  projects,
  duplicateId,
  totalQuantity,
  setCurrentAction,
  setProjectsQuantity,
  setProjects,
  saveDisabled,
  setSaveDisabled,
  setDuplicateSaved,
  setProjectDetails,
  currentAction,
  closeSnackbar
}) {
  const classes = useStyles();
  const nameInputRef = useRef();
  const [disabled, setDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [nameError, setNameError] = useState("");

  const { enqueueSnackbar } = useSnackbar();

  const activeProject = sessionStorage.currentProject;

  const handleChange = e => {
    const isValidProjectName = !checkIsURL(e.target.value);

    if (!isValidProjectName) {
      enqueueSnackbar(
        "The project name can not be a link, please write valid name for the project"
      );
      return;
    }

    setName(e.target.value);
    setSaveDisabled(false);
    if (name.trim() && selectedDate) setDisabled(false);
  };

  const handleSave = () => {
    if (!name.trim()) {
      setNameError("Please fill in this field");
      return;
    }

    setLoading(true);
    if (name.length > 255) {
      enqueueSnackbar("Project name is too long.");
      setLoading(false);
      return;
    }

    switch (currentAction) {
      case "duplicate":
        handleDuplicate();
        break;
      case "edit":
        handleUpdate();
        break;
      default:
        handleAdd();
    }
  };

  const handleDateChange = val => {
    setSelectedDate(val);
    setSaveDisabled(false);
  };

  const [changesDialog, setChangesDialog] = useState({
    visible: false,
    action: () => setCurrentAction("overview")
  });

  const handleUpdate = (isDuplicate, data) => {
    const id = data ? data.id : currentProject.id;
    const projectDate = Number.isInteger(selectedDate)
      ? selectedDate
      : selectedDate.split("/").reverse().join("-");

    API.patch(`/v1/projects/${id}`, {
      name,
      date: dateFns.format(new Date(projectDate), "dd/MM/yyyy")
    })
      .then(res => {
        const duplicated = data ? [{ ...currentProject, ...data }] : [];
        const newData = [...duplicated, ...projects].map(item =>
          item.id === id ? { ...item, name, project_date: projectDate } : item
        );

        setProjects(newData);
        if (!isDuplicate) enqueueSnackbar(`Project "${name}" edited.`);
        if (id === activeProject) {
          setProjectDetails(name, projectDate);
        }

        duplicateId && setDuplicateSaved(true);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(`Something went wrong, couldn't update the project.`);
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  const handleDuplicate = () => {
    const snackbar = enqueueSnackbar(
      `Creating copy of "${currentProject.name}"...`
    );

    API.post(`/v1/projects/${currentProject.id}/clone`)
      .then(res => {
        isEditingDuplicate.current = res.data.id;

        setProjectsQuantity(totalQuantity + 1);

        enqueueSnackbar(`Project "${name}" created.`);
        closeSnackbar(snackbar);

        handleUpdate(true, res.data);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(
          `Something went wrong, couldn't duplicate the project.`
        );
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  const handleAdd = () => {
    API.post("/v1/projects", {
      name,
      date: dateFns.format(new Date(getDateValue(selectedDate)), "dd/MM/yyyy"),
      enable_vehicles: true
    })
      .then(res => {
        if (!isApp || projects.length) {
          setProjects([{ ...res.data, id: res.data.id }, ...projects]);
          setProjectsQuantity(totalQuantity + 1);
        }
        enqueueSnackbar(`Project "${name}" created.`);
      })
      .catch(err => {
        console.log(err);
        enqueueSnackbar(`Something went wrong, couldn't create a project.`);
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  // Close edit panel
  const handleClose = () => {
    setSaveDisabled(true);
    setCurrentAction("overview");
  };

  const warnClose = saveDisabled
    ? handleClose
    : () =>
        setChangesDialog({
          visible: true,
          action: handleClose
        });

  useEffect(() => {
    if (currentProject) {
      let value;
      if (!Number.isInteger(currentProject.project_date)) {
        value = currentProject.project_date.split("/").reverse().join("-");
      } else {
        value = currentProject.project_date;
      }

      const name =
        currentAction === "duplicate"
          ? `Copy of ${currentProject.name}`
          : currentProject.name;
      const date = Math.max(new Date(), new Date(value));

      setName(name);
      setSelectedDate(date);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  useEffect(() => {
    if (currentAction !== "duplicate") {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject]);

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={locale}>
      <StyledDrawer isApp open={open} loading={loading} handleClose={warnClose}>
        {/* Header */}
        <Box
          px={3}
          className={classes.header}
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
        >
          <OptimizedTooltip placement="bottom" title="Close">
            <Box ml={-1} mr={2}>
              <IconButton onClick={warnClose} data-cy="driver-closeEdit">
                <BackIcon />
              </IconButton>
            </Box>
          </OptimizedTooltip>
          <Box alignSelf="center">
            <Typography variant="h5" className={classes.title}>
              {!currentProject
                ? "New project"
                : currentAction === "duplicate"
                ? "Duplicate project"
                : "Edit project"}
            </Typography>
          </Box>
        </Box>
        <Box pt={3} pl={3} pr={5} mb={1.5}>
          <TextField
            type="text"
            label="Project name"
            name="name"
            margin="dense"
            value={name || ""}
            error={!!nameError}
            id="edit-name-table"
            inputProps={{
              "data-cy": "projects-nameInput"
            }}
            helperText={nameError}
            className={classes.inputField}
            onChange={handleChange}
            inputRef={nameInputRef}
          />

          <CustomDatePicker
            value={selectedDate}
            classes={classes}
            name={name}
            currentAction={currentAction}
            setDisabled={setDisabled}
            currentProject={currentProject}
            onChange={handleDateChange}
            duplicate={isEditingDuplicate}
          />
        </Box>
        <EditDialogActions
          section="projects"
          handleClose={warnClose}
          handleClick={handleSave}
          disabled={(currentAction !== "duplicate" && disabled) || saveDisabled}
        />
      </StyledDrawer>
      <UnsavedChangesDialog
        open={changesDialog.visible}
        handleClose={() => {
          setChangesDialog({ visible: false, action: () => {} });
        }}
        action={() => {
          changesDialog.action();
          setSaveDisabled(true);
        }}
      />
    </MuiPickersUtilsProvider>
  );
}

const mapStateToProps = state => ({
  projects: state.account.projects,
  totalQuantity: state.account.totalQuantity
});

export default connect(mapStateToProps, {
  setProjects,
  setProjectsQuantity,
  setProjectDetails,
  closeSnackbar
})(ProjectsEditDialog);
